import classnames from "classnames";
import { columnAt, trackScale } from "modules/spending/workload-plan/components/plan-geometry";
import {
    columnLeft,
    columnsWidth,
    PlanGrid,
    snapEnd,
    snapStart,
} from "modules/spending/workload-plan/models/plan-scale";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { trackStyles } from "./PlanTrack";

/** Что тянут: полосу целиком или один из краёв */
export type BarMode = "move" | "start" | "end";

/** Сколько пикселей надо протащить, чтобы нажатие стало перетаскиванием, а не кликом */
const DRAG_THRESHOLD = 3;

interface DraggableBarProps {
    grid: PlanGrid;
    /** Видимые границы полосы в колонках */
    start: number;
    length: number;
    /** Режим правки и право на этот объект */
    enabled: boolean;
    /** Какие края можно тянуть: край в прошлом или за экраном не тянется */
    edges?: { start?: boolean; end?: boolean };
    /** Тянется только за края, тело полосы не переносится (хвост планового срока) */
    moveDisabled?: boolean;
    className: string;
    style: React.CSSProperties;
    tip?: string;
    tipNote?: string;
    /** Конец связи, который представляет полоса (endKey): к нему крепятся стрелки */
    linkEnd?: string;
    /** От полосы можно протянуть связь — у её концов появляются точки (рисует LinksLayer) */
    linkConnectable?: boolean;
    children?: React.ReactNode;
    /**
     * Жест закончен. delta — сдвиг в колонках (они же рабочие дни): для «move» — всей
     * полосы, для краёв — того края, за который тянули. Пока промис не выполнен,
     * полоса стоит там, куда её бросили.
     */
    onCommit: (mode: BarMode, delta: number) => Promise<unknown> | void;
}

/** Куда полоса встанет: колонки после жеста */
interface Target {
    start: number;
    length: number;
}

interface Gesture {
    mode: BarMode;
    /** Сдвиг курсора в логических px — полоса идёт за ним без привязки к сетке */
    dx: number;
    /** Место приземления с привязкой к сетке */
    target: Target;
}

/**
 * Полоса графика, которую в режиме правки можно перетащить или потянуть за край —
 * как блок в Табеле План.
 *
 * При переносе полоса идёт за курсором свободно, а оранжевый призрак показывает, куда
 * она встанет: шаг — колонка по дням и неделя по неделям. Края тянутся сразу по сетке.
 * Отпустили — полоса остаётся на месте приземления, пока сервер не пересчитает черновик,
 * и не прыгает назад. Клик без перетаскивания выделяет полосу оранжевой рамкой.
 */
export const DraggableBar: React.FC<DraggableBarProps> = (props) => {
    const {
        grid,
        start,
        length,
        enabled,
        edges,
        moveDisabled,
        className,
        style,
        tip,
        tipNote,
        linkEnd,
        linkConnectable,
        children,
        onCommit,
    } = props;

    const ref = useRef<HTMLDivElement>(null);
    const [gesture, setGesture] = useState<Gesture | null>(null);
    const [pending, setPending] = useState<Target | null>(null);
    const [selected, setSelected] = useState(false);
    const cleanup = useRef<(() => void) | undefined>(undefined);

    useEffect(() => () => cleanup.current && cleanup.current(), []);

    // выделение снимается кликом мимо и Esc — как в Табеле План
    useEffect(() => {
        if (!selected) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && setSelected(false);
        const onPointerDown = (event: PointerEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setSelected(false);
            }
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown, true);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [selected]);

    const targetAt = useCallback(
        (mode: BarMode, origin: number, column: number): Target => {
            const week = grid.scale === "week";
            const end = start + length - 1;

            if (mode === "move") {
                const delta = week ? snapStart(grid, column) - snapStart(grid, origin) : column - origin;
                return { start: start + delta, length };
            }

            if (mode === "end") {
                const last = Math.max(week ? snapEnd(grid, column) : column, start);
                return { start, length: last - start + 1 };
            }

            const first = Math.min(week ? snapStart(grid, column) : column, end);
            return { start: first, length: end - first + 1 };
        },
        [grid, start, length]
    );

    const onPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            const element = ref.current;
            const track = element && element.parentElement;

            if (!enabled || pending || event.button !== 0 || !element || !track) {
                return;
            }

            const edge = (event.target as HTMLElement).getAttribute("data-edge");
            const mode: BarMode = edge === "start" ? "start" : edge === "end" ? "end" : "move";

            // тело такой полосы не берётся — клик по нему работает как обычно
            if (mode === "move" && moveDisabled) {
                return;
            }

            const origin = columnAt(track, event.clientX, grid);

            if (origin < 0) {
                return;
            }

            // клик по полосе проекта сворачивает его — жест не должен
            event.stopPropagation();
            event.preventDefault();

            const startX = event.clientX;
            // зум страницы: смещение курсора переводим в логические px сетки
            const scale = trackScale(track, grid) || 1;

            let active = false;
            let target: Target = { start, length };

            const onMove = (move: PointerEvent) => {
                if (!active && Math.abs(move.clientX - startX) < DRAG_THRESHOLD) {
                    return;
                }

                active = true;

                const column = columnAt(track, move.clientX, grid);

                if (column >= 0) {
                    target = targetAt(mode, origin, column);
                }

                setGesture({ mode, dx: (move.clientX - startX) / scale, target });
            };

            const finish = () => {
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
                window.removeEventListener("pointercancel", onCancel);
                cleanup.current = undefined;
                setGesture(null);
            };

            const onUp = () => {
                finish();

                if (!active) {
                    // клик без перетаскивания — выделение
                    setSelected((value) => !value);
                    return;
                }

                // клик, который браузер пришлёт следом за перетаскиванием, никому не нужен
                const swallow = (click: MouseEvent) => {
                    click.stopPropagation();
                    click.preventDefault();
                };
                window.addEventListener("click", swallow, { capture: true, once: true });
                window.setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 0);

                const delta =
                    mode === "move"
                        ? target.start - start
                        : mode === "end"
                          ? target.start + target.length - (start + length)
                          : target.start - start;

                if (delta === 0) {
                    return;
                }

                setSelected(true);

                // полоса стоит на месте приземления, пока сервер не ответит
                const done = onCommit(mode, delta);

                if (done && typeof (done as Promise<unknown>).then === "function") {
                    setPending(target);
                    (done as Promise<unknown>).finally(() => setPending(null));
                }
            };

            const onCancel = () => finish();

            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            window.addEventListener("pointercancel", onCancel);
            cleanup.current = finish;
        },
        [enabled, pending, moveDisabled, grid, start, length, targetAt, onCommit]
    );

    // где полоса стоит сейчас: у жеста переноса — за курсором, у краёв — по сетке,
    // после отпускания — на месте приземления
    const placed = pending || (gesture && gesture.mode !== "move" ? gesture.target : null) || { start, length };

    const left = columnLeft(grid, placed.start);
    const width = columnsWidth(grid, placed.start, placed.length);

    const moving = !!gesture && gesture.mode === "move";

    return (
        <>
            {moving && gesture && (
                <div
                    className={trackStyles.dropGhost}
                    style={{
                        left: columnLeft(grid, gesture.target.start),
                        width: columnsWidth(grid, gesture.target.start, gesture.target.length),
                    }}
                />
            )}

            <div
                ref={ref}
                className={classnames(className, {
                    [trackStyles.editable]: enabled && !moveDisabled,
                    [trackStyles.dragging]: !!gesture,
                    // приземление без анимации: иначе полоса, отпущенная с курсора,
                    // сначала отскочила бы к старому месту и оттуда доехала до нового
                    [trackStyles.settling]: !!pending,
                    [trackStyles.selected]: selected && !gesture,
                })}
                style={{
                    ...style,
                    left,
                    width,
                    transform: moving && gesture ? `translateX(${gesture.dx}px)` : undefined,
                }}
                data-tip={gesture ? undefined : tip}
                data-tip-note={gesture ? undefined : tipNote}
                data-link-end={linkEnd}
                data-link-connectable={linkConnectable || undefined}
                onPointerDown={enabled ? onPointerDown : undefined}
            >
                {children}

                {enabled && edges && edges.start && <div className={trackStyles.handleLeft} data-edge="start" />}
                {enabled && edges && edges.end && <div className={trackStyles.handleRight} data-edge="end" />}
            </div>
        </>
    );
};
